import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaFacebookF, FaWhatsapp } from "react-icons/fa";
import {
  FiArrowUpRight,
  FiAward,
  FiCheck,
  FiClock,
  FiCopy,
  FiGift,
  FiLink,
  FiShare2,
  FiUsers,
  FiX,
} from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Get from "../services/Get";
import "./ReferralDashboard.css";

const claimPath = "/referral/earn-together/refer2earn-usdc/claim";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

const formatAmount = (amount) => Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 8 });

const normalizeLedgerResponse = (response) => {
  if (Array.isArray(response?.data)) return response;
  const payload = response?.data ?? response;
  if (Array.isArray(payload?.data)) return payload;
  if (Array.isArray(payload)) return { data: payload, current_page: 1, last_page: 1 };
  return { data: [], current_page: 1, last_page: 1 };
};

const formatDate = (date) => {
  if (!date) return "—";
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString();
};

const campaignTasks = [
  {
    number: "01",
    title: "Invite a friend",
    description: "Share your personal referral link with someone new to Tillionx.",
    icon: FiShare2,
  },
  {
    number: "02",
    title: "They create an account",
    description: "Your friend joins Tillionx using your referral link.",
    icon: FiUsers,
  },
  {
    number: "03",
    title: "Complete a qualifying activity",
    description: "When the campaign requirements are met, eligible rewards appear here.",
    icon: FiAward,
  },
];

const emptyMessages = {
  progress: {
    title: "Your invite activity will appear here",
    description: "Share your referral link to get started.",
  },
  referrals: {
    title: "No referrals yet",
    description: "Friends who join through your link will show up here.",
  },
  rewards: {
    title: "No rewards yet",
    description: "Eligible rewards will appear here after campaign requirements are met.",
  },
};

function ReferralDashboard({ embedded = false }) {
  const [user] = useState(getStoredUser);
  const [summary, setSummary] = useState(null);
  const [ledgerResponse, setLedgerResponse] = useState({ data: [], current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [ledgerPage, setLedgerPage] = useState(1);
  const [activeTab, setActiveTab] = useState("progress");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const referralCode = summary?.referral_code || user?.referral_code || "";
  const referralLink = referralCode
    ? `${window.location.origin}${claimPath}?hl=en-IN&ref=${encodeURIComponent(referralCode)}&utm_source=referral_entrance`
    : "";
  const ledger = ledgerResponse.data || [];
  const visibleLedger = activeTab === "rewards"
    ? ledger.filter((entry) => ["pending", "approved", "credited"].includes(entry.status))
    : ledger;
  const totalReferrals = summary?.total_referrals ?? 0;
  const qualifiedReferrals = summary?.qualified_referrals ?? 0;

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");

    if (!token) {
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    setLoadError("");
    Promise.allSettled([
      Get.ReferralDashboard(),
      Get.MonthlyRewardReferrals({ page: ledgerPage }),
    ]).then(([summaryResult, ledgerResult]) => {
      if (cancelled) return;

      const errors = [];
      if (summaryResult.status === "fulfilled" && summaryResult.value?.success !== false) {
        setSummary(summaryResult.value?.data ?? summaryResult.value);
      } else {
        errors.push(summaryResult.status === "rejected" ? summaryResult.reason : new Error("Could not load referral summary."));
      }

      if (ledgerResult.status === "fulfilled" && ledgerResult.value?.success !== false) {
        setLedgerResponse(normalizeLedgerResponse(ledgerResult.value));
      } else {
        errors.push(ledgerResult.status === "rejected" ? ledgerResult.reason : new Error("Could not load referral activity."));
      }

      if (errors.length) setLoadError(errors.map((error) => error.message).join(" "));
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [ledgerPage]);

  const showNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2400);
  };

  const copyValue = async (value, label) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(value);
      showNotice(`${label} copied`);
    } catch {
      const input = document.createElement("textarea");
      input.value = value;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand("copy");
      input.remove();
      showNotice(copied ? `${label} copied` : "Unable to copy. Select and copy the link.");
    }
  };

  const shareBy = (platform) => {
    const message = encodeURIComponent(`Join me on Tillionx: ${referralLink}`);
    const shareUrls = {
      WhatsApp: `https://wa.me/?text=${message}`,
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      X: `https://twitter.com/intent/tweet?text=${message}`,
    };
    window.open(shareUrls[platform], "_blank", "noopener,noreferrer");
  };

  const handleNativeShare = async () => {
    if (!referralLink) {
      showNotice("Sign in to load your personal referral code.");
      return;
    }
    if (!navigator.share) {
      await copyValue(referralLink, "Referral link");
      return;
    }
    try {
      await navigator.share({
        title: "Join me on Tillionx",
        text: "Create an account with my referral link.",
        url: referralLink,
      });
    } catch (error) {
      if (error.name !== "AbortError") showNotice("Sharing is unavailable right now.");
    }
  };

  const currentEmptyState = emptyMessages[activeTab];

  return (
    <div className="referral-page">
      {!embedded && <Header />}
      <main className="referral-main">
        <div className="referral-page-heading">
          <div>
            <p className="referral-eyebrow">TILLIONX REWARDS</p>
            <h1>Refer friends</h1>
            <p className="referral-subtitle">Invite people you know and follow your referral activity in one place.</p>
          </div>
          <button className="referral-button referral-button-primary" type="button" onClick={() => setInviteOpen(true)}>
            <FiShare2 aria-hidden="true" /> Invite friends
          </button>
        </div>

        <section className="referral-hero" aria-labelledby="referral-hero-title">
          <div className="referral-hero-copy">
            <span className="referral-campaign-tag"><FiGift aria-hidden="true" /> REFERRAL LITE</span>
            <h2 id="referral-hero-title">Good things travel<br />better together.</h2>
            <p>Share Tillionx with your friends. Any eligible rewards will be tracked here.</p>
            <div className="referral-total">
              <span>Total rewards earned</span>
              <strong>{summary ? formatAmount(summary.credited_amount) : "—"} <small>units</small></strong>
              <span className="referral-total-note"><FiClock aria-hidden="true" /> {loading ? "Loading your referral rewards" : "Credited referral ledger amount"}</span>
            </div>
          </div>
          <div className="referral-share-panel">
            <div className="referral-share-panel-heading">
              <div>
                <span className="referral-field-label">YOUR INVITE LINK</span>
                <h3>Invite a friend</h3>
              </div>
              <span className="referral-link-icon"><FiLink aria-hidden="true" /></span>
            </div>
            <label className="referral-copy-field">
              <span className="sr-only">Your referral link</span>
              <input readOnly value={referralLink || (loading ? "Loading your referral code…" : "Sign in to load your referral link")} onFocus={(event) => event.target.select()} />
              <button type="button" disabled={!referralLink} onClick={() => copyValue(referralLink, "Referral link")} aria-label="Copy referral link" title="Copy referral link">
                <FiCopy aria-hidden="true" />
              </button>
            </label>
            <div className="referral-code-row">
              <span>Referral code <strong>{referralCode || (loading ? "Loading…" : "Unavailable")}</strong></span>
              <button type="button" disabled={!referralCode} onClick={() => copyValue(referralCode, "Referral code")}>Copy code</button>
            </div>
            <button className="referral-button referral-button-light" type="button" onClick={handleNativeShare}>
              <FiShare2 aria-hidden="true" /> Share invite link <FiArrowUpRight aria-hidden="true" />
            </button>
          </div>
        </section>

        <section className="referral-summary" aria-label="Referral summary">
          <article className="referral-stat">
            <span><FiUsers aria-hidden="true" /> Friends invited</span>
            <strong>{loading ? "—" : totalReferrals}</strong>
            <small>People who joined with your link</small>
          </article>
          <article className="referral-stat">
            <span><FiCheck aria-hidden="true" /> Qualified referrals</span>
            <strong>{loading ? "—" : qualifiedReferrals}</strong>
            <small>Invites that met campaign requirements</small>
          </article>
          <article className="referral-stat">
            <span><FiClock aria-hidden="true" /> Pending rewards</span>
            <strong>{summary ? formatAmount(summary.pending_amount) : "—"} <small>units</small></strong>
            <small>Pending and approved referral ledger amounts</small>
          </article>
        </section>

        <section className="referral-tasks" aria-labelledby="referral-tasks-title">
          <div className="referral-section-heading">
            <div>
              <p className="referral-eyebrow">HOW IT WORKS</p>
              <h2 id="referral-tasks-title">Three steps to get started</h2>
            </div>
            <span className="referral-terms-note">Campaign terms apply</span>
          </div>
          <div className="referral-task-grid">
            {campaignTasks.map(({ number, title, description, icon: Icon }) => (
              <article className="referral-task" key={number}>
                <div className="referral-task-topline"><span>{number}</span><Icon aria-hidden="true" /></div>
                <h3>{title}</h3>
                <p>{number === "02" ? `${totalReferrals} friends have joined using your referral.` : number === "03" ? `${qualifiedReferrals} referrals are approved or credited.` : description}</p>
                {number === "01" && <button type="button" onClick={() => setInviteOpen(true)}>Invite now <FiArrowUpRight aria-hidden="true" /></button>}
                {number !== "01" && <span className={`referral-task-status ${((number === "02" && totalReferrals > 0) || (number === "03" && qualifiedReferrals > 0)) ? "is-complete" : ""}`}><FiClock aria-hidden="true" /> {((number === "02" && totalReferrals > 0) || (number === "03" && qualifiedReferrals > 0)) ? "Activity recorded" : "Waiting for invite"}</span>}
              </article>
            ))}
          </div>
        </section>

        <section className="referral-activity" aria-labelledby="referral-activity-title">
          <div className="referral-section-heading">
            <div>
              <p className="referral-eyebrow">YOUR ACCOUNT</p>
              <h2 id="referral-activity-title">Referral activity</h2>
            </div>
            <div className="referral-tabs" role="tablist" aria-label="Referral activity">
              {[["progress", "My progress"], ["referrals", "My referrals"], ["rewards", "My rewards"]].map(([tab, label]) => (
                <button key={tab} type="button" role="tab" aria-selected={activeTab === tab} className={activeTab === tab ? "is-active" : ""} onClick={() => setActiveTab(tab)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="referral-empty-state" role="tabpanel">
            {loading ? <p>Loading referral activity…</p> : loadError ? <><h3>Some referral data is unavailable</h3><p>{loadError}</p></> : !localStorage.getItem("token") ? <><h3>Sign in to see your referral account</h3><p>Your personal code, invite activity, and rewards are only available after you sign in.</p><Link to="/login">Log in <FiArrowUpRight aria-hidden="true" /></Link></> : visibleLedger.length ? (
              <div className="referral-ledger-wrap">
                <table className="referral-ledger">
                  <thead><tr><th>Referral</th><th>Source</th><th>Plan</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>{visibleLedger.map((entry) => <tr key={entry.id}>
                    <td>#{entry.referred_id}</td>
                    <td>{(entry.source_type || "Referral").replaceAll("_", " ")}</td>
                    <td>{entry.plan || "—"}</td>
                    <td>{formatAmount(entry.amount)} units</td>
                    <td><span className={`referral-status referral-status-${entry.status}`}>{entry.status}</span></td>
                    <td>{formatDate(entry.created_at)}</td>
                  </tr>)}</tbody>
                </table>
                <div className="referral-pagination">
                  <span>Page {ledgerResponse.current_page || 1} of {ledgerResponse.last_page || 1}</span>
                  <div><button type="button" disabled={(ledgerResponse.current_page || 1) <= 1 || loading} onClick={() => setLedgerPage((page) => page - 1)}>Previous</button><button type="button" disabled={(ledgerResponse.current_page || 1) >= (ledgerResponse.last_page || 1) || loading} onClick={() => setLedgerPage((page) => page + 1)}>Next</button></div>
                </div>
              </div>
            ) : <><span className="referral-empty-icon">{activeTab === "rewards" ? <FiGift aria-hidden="true" /> : <FiUsers aria-hidden="true" />}</span><h3>{activeTab === "rewards" ? emptyMessages.rewards.title : activeTab === "referrals" ? emptyMessages.referrals.title : emptyMessages.progress.title}</h3><p>{activeTab === "rewards" ? emptyMessages.rewards.description : activeTab === "referrals" ? emptyMessages.referrals.description : emptyMessages.progress.description}</p>{activeTab === "progress" && <button type="button" onClick={() => setInviteOpen(true)}>Invite your first friend <FiArrowUpRight aria-hidden="true" /></button>}</>}
          </div>
        </section>

        <details className="referral-rules">
          <summary>Referral program details <span>View rules</span></summary>
          <div>
            <p>Referral rewards depend on the active Tillionx campaign, account eligibility, and completion of its qualifying steps. Rewards are not guaranteed by sharing a link alone.</p>
            <p>Current reward amounts, qualification windows, and exclusions will be shown in the campaign terms when available.</p>
          </div>
        </details>
      </main>
      {!embedded && <Footer />}

      {notice && <div className="referral-toast" role="status" aria-live="polite">{notice}</div>}

      {inviteOpen && (
        <div className="referral-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setInviteOpen(false); }}>
          <section className="referral-modal" role="dialog" aria-modal="true" aria-labelledby="invite-modal-title">
            <button className="referral-modal-close" type="button" onClick={() => setInviteOpen(false)} aria-label="Close invite dialog"><FiX aria-hidden="true" /></button>
            <span className="referral-modal-icon"><FiUsers aria-hidden="true" /></span>
            <p className="referral-eyebrow">TILLIONX REFERRALS</p>
            <h2 id="invite-modal-title">Bring your people along.</h2>
            <p className="referral-modal-copy">Send your personal invite link. Your friends can use it to create a Tillionx account.</p>
            <div className="referral-modal-link"><span>{referralLink || "Sign in to load your personal link"}</span><button type="button" disabled={!referralLink} onClick={() => copyValue(referralLink, "Referral link")} aria-label="Copy referral link"><FiCopy aria-hidden="true" /></button></div>
            <div className="referral-socials" aria-label="Share referral link">
              <button type="button" onClick={() => shareBy("WhatsApp")} aria-label="Share with WhatsApp" title="WhatsApp"><FaWhatsapp aria-hidden="true" /></button>
              <button type="button" onClick={() => shareBy("Facebook")} aria-label="Share with Facebook" title="Facebook"><FaFacebookF aria-hidden="true" /></button>
              <button type="button" onClick={() => shareBy("X")} aria-label="Share with X" title="X"><span aria-hidden="true">X</span></button>
              <button type="button" onClick={handleNativeShare} aria-label="More sharing options" title="More sharing options"><FiShare2 aria-hidden="true" /></button>
            </div>
            <button className="referral-button referral-button-primary referral-modal-share" type="button" onClick={handleNativeShare}>Share invite <FiArrowUpRight aria-hidden="true" /></button>
          </section>
        </div>
      )}
    </div>
  );
}

export default ReferralDashboard;
